import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import PanchangaSnapshot from '../shared/PanchangaSnapshot';
import PanchangaInterpretation from '../shared/PanchangaInterpretation';

const Card = styled.article`
  max-width: 640px;
  margin: 0 auto;
  padding: ${p => p.theme.spacing[5]}px;
  background: linear-gradient(180deg, #FFFBF0, #FFF3D6);
  border: 1px solid rgba(218,165,32,0.3);
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(139,105,20,0.08);
  text-align: center;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: ${p => p.theme.spacing[4]}px;
  }
`;

const Heading = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 22px;
  color: #8B4513;
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const CollectedStrip = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: ${p => p.theme.spacing[4]}px 0;
  flex-wrap: wrap;
`;

const CollectedLetter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 36px;
`;

const CollectedDev = styled.span`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 28px;
  font-weight: 600;
  color: #5D3A1A;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 22px;
  }
`;

const CollectedTa = styled.span`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 11px;
  color: #9B8B7A;
`;

const FullVerseDev = styled.p`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2C1810;
  margin: ${p => p.theme.spacing[4]}px 0 ${p => p.theme.spacing[2]}px;
`;

const FullVerseTa = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const MeaningHeading = styled.h3`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #8B6914;
  margin: ${p => p.theme.spacing[5]}px 0 ${p => p.theme.spacing[2]}px;
`;

const MeaningPrimary = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: #2C1810;
  margin: 0 0 ${p => p.theme.spacing[3]}px;
`;

const MeaningSecondary = styled.p`
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.colors.text.secondary};
  font-style: italic;
`;

const Actions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing[3]}px;
  justify-content: center;
  margin-top: ${p => p.theme.spacing[5]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    flex-direction: column;
  }
`;

const ShareButton = styled.button`
  padding: 12px 32px;
  background: linear-gradient(135deg, #DAA520, #B8860B);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const ReturnButton = styled.button`
  padding: 12px 32px;
  background: none;
  border: 1px solid #DAA520;
  color: #8B6914;
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  &:hover { background: rgba(218,165,32,0.08); }
`;

const ResultCard = ({ pathCells, verse, grid, gridTa, onShare, onReturn, lang, panchangaSnapshot }) => {
  const t = useLocale(lang, 'ramaShalaka');
  const primaryMeaning = lang === 'ta' ? verse.meaning_ta : verse.meaning_en;
  const secondaryMeaning = lang === 'ta' ? verse.meaning_en : verse.meaning_ta;
  const verseTone = verse.tone || 'mixed';

  return (
    <Card>
      <Heading>
        {t.resultHeading || "Sri Rama's Guidance"}
      </Heading>

      <CollectedStrip aria-label={t.collectedLettersAria || 'Collected letters from the path'}>
        {pathCells.map(idx => {
          const r = Math.floor(idx / 9);
          const c = idx % 9;
          return (
            <CollectedLetter key={idx}>
              <CollectedDev>{grid[r][c]}</CollectedDev>
              <CollectedTa>{gridTa[r][c]}</CollectedTa>
            </CollectedLetter>
          );
        })}
      </CollectedStrip>

      <FullVerseDev>{verse.devanagari}</FullVerseDev>
      <FullVerseTa>{verse.transliteration_ta}</FullVerseTa>

      <MeaningHeading>{t.meaningHeading || 'Meaning'}</MeaningHeading>
      <MeaningPrimary>{primaryMeaning}</MeaningPrimary>
      <MeaningSecondary>{secondaryMeaning}</MeaningSecondary>

      <PanchangaSnapshot snapshot={panchangaSnapshot} lang={lang} />
      <PanchangaInterpretation snapshot={panchangaSnapshot} verseTone={verseTone} lang={lang} />

      <Actions>
        <ShareButton onClick={onShare}>
          {t.shareButton || 'Share'}
        </ShareButton>
        <ReturnButton onClick={onReturn}>
          {t.returnButton || 'Return'}
        </ReturnButton>
      </Actions>
    </Card>
  );
};

export default ResultCard;
